import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../lib/AuthContext';
import { toast } from 'react-toastify';
import { WifiIcon, X, PlusIcon, ClockIcon, TrashIcon } from 'lucide-react';
import AvailabilityModal from '../components/AvailabilityModal';

const ExpertProfile = ({ onProfileUpdate, onCancel }) => {
  const { accessToken: token, user, setUser } = useAuth();
  const [form, setForm] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    headline: "",
    about: "",
    profile_picture: null, // Added for profile picture
  });
  const [isOnline, setIsOnline] = useState(false);
  const [availabilities, setAvailabilities] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  const fetchProfile = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`https://quirkleai.com/api/me/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch profile');
      const data = await response.json();
      setForm({
        username: data.username || "",
        email: data.email || "",
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        headline: data.headline || "",
        about: data.about || "",
        profile_picture: data.profile_picture || null,
      });
      console.log("Fetched profile picture URL:", data.profile_picture);
      setIsOnline(data.is_online);
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Could not load profile data.");
    }
  }, [token]);

  const fetchAvailabilities = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`https://quirkleai.com/api/availability/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch availability');
      const data = await response.json();
      setAvailabilities(data);
    } catch (error) {
      console.error("Error fetching availability:", error);
      toast.error("Could not load availability data.");
    }
  }, [token]);

  useEffect(() => {
    fetchProfile();
    fetchAvailabilities();
  }, [fetchProfile, fetchAvailabilities]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleAddAvailability = ({ day_of_week, start_time, end_time }) => {
    fetch('https://quirkleai.com/api/availability/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ day_of_week, start_time, end_time })
    })
      .then(res => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((newItem) => {
        setAvailabilities(prev => [...prev, newItem]);
        toast.success("Availability added!");
        fetchAvailabilities();
      })
      .catch(() => toast.error("Failed to add availability"))
      .finally(() => setIsModalOpen(false));
  };

  const handleDelete = (id) => {
    fetch(`https://quirkleai.com/api/availability/${id}/`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        if (!res.ok) throw new Error();
        toast.success("Availability deleted!");
        fetchAvailabilities();
      })
      .catch(() => toast.error("Failed to delete availability"));
  };

  const handleDeleteAll = () => {
    if (window.confirm("Are you sure you want to delete all your availability slots?")) {
      fetch('https://quirkleai.com/api/availability/delete_all/', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(res => {
          if (!res.ok) {
            throw new Error("Failed to delete all availabilities");
          }
          toast.success("All availability has been deleted.");
          fetchAvailabilities();
        })
        .catch(err => {
          toast.error(err.message);
        });
    }
  };

  const handleSave = async () => {
    try {
      const formData = new FormData();
      formData.append('first_name', form.first_name);
      formData.append('last_name', form.last_name);
      formData.append('headline', form.headline);
      formData.append('about', form.about);
      if (form.profile_picture instanceof File) {
        formData.append('profile_picture', form.profile_picture);
      }

      const response = await fetch(`https://quirkleai.com/api/me/`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      const updatedUser = await response.json();
      setUser(updatedUser); // Update user in AuthContext
      toast.success("Profile updated successfully!");
      if (onProfileUpdate) {
        onProfileUpdate();
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Edit Profile</h1>
        <Button variant="ghost" size="icon" onClick={onCancel}>
          <X className="h-6 w-6" />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left Column: Profile Form */}
        <Card className="bg-white shadow-sm border">
          <CardContent className="py-6 space-y-4">
            <label className="block text-sm font-medium text-gray-700">Username</label>
            <input
              name="username"
              value={form.username}
              onChange={handleChange}
              className="border rounded p-2 w-full"
              placeholder="Your username"
              disabled
            />
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              name="email"
              value={form.email}
              onChange={handleChange}
              className="border rounded p-2 w-full"
              placeholder="Your email"
              disabled
            />
            <label className="block text-sm font-medium text-gray-700">First Name</label>
            <input
              name="first_name"
              value={form.first_name}
              onChange={handleChange}
              className="border rounded p-2 w-full"
              placeholder="Your first name"
            />
            <label className="block text-sm font-medium text-gray-700">Last Name</label>
            <input
              name="last_name"
              value={form.last_name}
              onChange={handleChange}
              className="border rounded p-2 w-full"
              placeholder="Your last name"
            />
            <label className="block text-sm font-medium text-gray-700">Headline</label>
            <input
              name="headline"
              value={form.headline}
              onChange={handleChange}
              className="border rounded p-2 w-full"
              placeholder="Headline or title"
            />
            <label className="block text-sm font-medium text-gray-700">About</label>
            <textarea
              name="about"
              value={form.about}
              onChange={handleChange}
              rows="4"
              className="border rounded p-2 w-full"
              placeholder="Tell something about yourself..."
            />
          </CardContent>
        </Card>

        {/* Right Column: Availability, Profile Picture, Status, and Actions */}
        <div className="space-y-6">
          <Card className="bg-white shadow-sm border">
            <CardContent className="py-6 space-y-4">
              <label className="block text-sm font-medium text-gray-700">Profile Picture</label>
              {form.profile_picture && typeof form.profile_picture === 'string' && (
                <div className="my-2">
                  <img style={{ width: 40, height: 40 }} 
                    src={`http://localhost:8000${form.profile_picture}`}
                    alt="Current Profile"
                    className="rounded-full object-cover"
                  />
                </div>
              )}
              <input
                type="file"
                name="profile_picture"
                onChange={(e) => setForm({ ...form, profile_picture: e.target.files[0] })}
                className="border rounded p-2 w-full"
              />
              
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <WifiIcon size={16} />
                Status: {isOnline ? <span className="text-green-600">Online</span> : <span className="text-red-600">Offline</span>}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm border">
            <CardContent className="py-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Your Recurring Availability</h2>
                <div className="flex items-center gap-2">
                  <Button onClick={() => setIsModalOpen(true)}>
                    <PlusIcon size={16} className="mr-2" />
                    Add
                  </Button>
                  {availabilities.length > 0 && (
                    <Button variant="destructive" onClick={handleDeleteAll}>
                      <TrashIcon size={16} className="mr-2" />
                      Delete All
                    </Button>
                  )}
                </div>
              </div>

              {availabilities.length === 0 && (
                <p className="text-sm text-gray-500">You have not set any recurring availability yet.</p>
              )}

              <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4">
                {availabilities.map((slot) => (
                  <Card key={slot.id} className="bg-white border shadow-sm rounded-lg">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex justify-between items-center">
                        <div className="text-sm text-gray-700 flex items-center gap-2">
                          <ClockIcon size={14} />
                          {daysOfWeek[slot.day_of_week]}: {slot.start_time} - {slot.end_time}
                        </div>
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(slot.id)}>
                          <TrashIcon size={14} />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col items-center gap-2 mt-4">
            <Button variant="outline" onClick={handleSave}>Save Changes</Button>
            <Button variant="outline" onClick={onCancel}>Cancel</Button>
          </div>
        </div>
      </div>

      <AvailabilityModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleAddAvailability}
      />
    </div>
  );
};

export default ExpertProfile;
